import React, { useState, useRef } from "react";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import SearchIcon from "@mui/icons-material/Search";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";

// Destinatário de faturamento (NF em nome de terceiro) — busca/cadastra contato no
// Bling. Compartilhado entre OS (ServiceOrderDialog) e Orçamento (QuoteDialog) para
// garantir paridade dos campos na conversão orçamento→OS.
// value: { billingBlingContactId, billingName, billingDocument }
const BlingBillingRecipient = ({ value, onChange }) => {
  const { billingBlingContactId, billingName, billingDocument } = value;
  const [searchText, setSearchText] = useState("");
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDoc, setNewDoc] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const [noResults, setNoResults] = useState(false);
  const timer = useRef(null);

  const selected = !!billingBlingContactId;

  const doSearch = (text) => {
    clearTimeout(timer.current);
    setNoResults(false);
    if (!text || text.trim().length < 2) { setOptions([]); setOpen(false); return; }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/bling/contacts/search", { params: { nome: text.trim() } });
        const list = Array.isArray(data) ? data : [];
        setOptions(list);
        setNoResults(list.length === 0);
        setOpen(true);
      } catch { setOptions([]); }
      finally { setLoading(false); }
    }, 350);
  };

  const handleSelect = (opt) => {
    onChange({ billingBlingContactId: opt.id, billingName: opt.nome, billingDocument: opt.numeroDocumento || "" });
    setOpen(false); setSearchText(""); setOptions([]); setNoResults(false);
  };

  const handleCreate = async () => {
    if (!newName.trim()) { toast.warn("Informe o nome / razão social."); return; }
    const docDigits = newDoc.replace(/\D/g, "");
    if (docDigits && docDigits.length !== 11 && docDigits.length !== 14) {
      toast.warn("CPF deve ter 11 dígitos ou CNPJ 14."); return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/bling/contacts/billing", {
        nome: newName.trim(), documento: newDoc.trim(), phone: newPhone.trim(),
      });
      toast.success("Destinatário de faturamento pronto no Bling.");
      onChange({ billingBlingContactId: data.id, billingName: data.nome, billingDocument: data.numeroDocumento || docDigits });
      setCreating(false); setNewName(""); setNewDoc(""); setNewPhone("");
    } catch (err) { toastError(err); } finally { setSaving(false); }
  };

  const handleClear = () => {
    onChange({ billingBlingContactId: null, billingName: "", billingDocument: "" });
    setSearchText(""); setCreating(false);
  };

  if (selected && !creating) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <Chip
          label={`🧾 ${billingName}${billingDocument ? ` · ${billingDocument}` : " · sem CPF/CNPJ"}`}
          onDelete={handleClear}
          style={{ backgroundColor: billingDocument ? "#e8f5e9" : "#fff3e0", color: billingDocument ? "#1b5e20" : "#C05000", fontWeight: 600, maxWidth: 380 }}
        />
        {!billingDocument && (
          <span style={{ fontSize: 11, color: "#e65100" }}>⚠️ sem documento — NF pode sair incorreta</span>
        )}
      </div>
    );
  }

  if (creating) {
    return (
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <TextField label="Nome / Razão social *" size="small" style={{ flex: 2, minWidth: 180 }}
          value={newName} onChange={e => setNewName(e.target.value)} autoFocus />
        <TextField label="CPF / CNPJ *" size="small" style={{ flex: 1, minWidth: 150 }}
          value={newDoc} onChange={e => setNewDoc(e.target.value)} placeholder="só números" />
        <TextField label="Telefone (opcional)" size="small" style={{ flex: 1, minWidth: 130 }}
          value={newPhone} onChange={e => setNewPhone(e.target.value)} />
        <Button variant="contained" size="small" onClick={handleCreate} disabled={saving}
          style={{ backgroundColor: "#F37021", color: "#fff", whiteSpace: "nowrap" }}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <PersonAddIcon />}>
          Cadastrar
        </Button>
        <Button size="small" onClick={() => { setCreating(false); setNewName(""); setNewDoc(""); setNewPhone(""); }}>
          Cancelar
        </Button>
      </div>
    );
  }

  return (
    <div style={{ position: "relative" }}>
      <TextField
        label="Buscar empresa/pessoa no Bling (nome ou CPF/CNPJ)" size="small" fullWidth
        value={searchText}
        onChange={e => { setSearchText(e.target.value); doSearch(e.target.value); }}
        onBlur={() => setTimeout(() => { setOpen(false); setNoResults(false); }, 200)}
        placeholder="Digite nome, razão social ou documento..."
        InputProps={{
          endAdornment: loading
            ? <CircularProgress size={14} style={{ color: "#F37021" }} />
            : <SearchIcon fontSize="small" style={{ color: "#bbb" }} />,
        }}
      />
      {open && options.length > 0 && (
        <div style={{
          position: "absolute", zIndex: 1400, top: "100%", left: 0, right: 0,
          background: "#fff", border: "1px solid #ddd", borderRadius: 6,
          boxShadow: "0 4px 16px rgba(0,0,0,.12)", maxHeight: 280, overflowY: "auto",
        }}>
          {options.map(opt => (
            <div key={opt.id} onMouseDown={() => handleSelect(opt)}
              style={{ padding: "8px 12px", cursor: "pointer", borderBottom: "1px solid #f5f5f5" }}
              onMouseEnter={e => e.currentTarget.style.background = "#fff8f4"}
              onMouseLeave={e => e.currentTarget.style.background = "#fff"}>
              <div style={{ fontWeight: 600, fontSize: 13, color: "#222" }}>{opt.nome}</div>
              <div style={{ fontSize: 11, color: opt.numeroDocumento ? "#1b5e20" : "#e65100", marginTop: 2 }}>
                {opt.numeroDocumento ? `Doc: ${opt.numeroDocumento}` : "⚠️ sem CPF/CNPJ no Bling"}
                {opt.celular ? ` · ${opt.celular}` : ""}
              </div>
            </div>
          ))}
        </div>
      )}
      {noResults && !loading && searchText.trim().length >= 2 && (
        <div style={{
          position: "absolute", zIndex: 1400, top: "100%", left: 0, right: 0,
          background: "#fff", border: "1px solid #ddd", borderRadius: 6,
          boxShadow: "0 4px 16px rgba(0,0,0,.12)", padding: "10px 14px",
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
        }}>
          <span style={{ fontSize: 12, color: "#999" }}>Nenhum contato encontrado.</span>
          <Button size="small" variant="outlined"
            onClick={() => { setOpen(false); setCreating(true); setNewName(searchText); }}
            startIcon={<PersonAddIcon />}
            style={{ borderColor: "#F37021", color: "#F37021", fontSize: 11 }}>
            Cadastrar novo
          </Button>
        </div>
      )}
      {!open && !noResults && (
        <div style={{ marginTop: 4, textAlign: "right" }}>
          <Button size="small" onClick={() => setCreating(true)} startIcon={<PersonAddIcon />}
            style={{ color: "#F37021", fontSize: 11, padding: "0 4px" }}>
            Cadastrar novo destinatário
          </Button>
        </div>
      )}
    </div>
  );
};

export default BlingBillingRecipient;
